import { createSelector } from 'reselect'
import type { Artist, ArtistId } from 'types/artist'
import type { PlaylistId } from 'types/playlist'
import type { TrackId } from 'types/track'
import type { State } from 'types/redux'
import type { State as ArtistsState } from '@disco/state-management/reducers/artists'
import { getPlaylists } from '@disco/state-management/selectors/playlists'
import { getTracks } from '@disco/state-management/selectors/tracks'
/**
 * Gets a specific artist by ID.
 * @param state - The Redux state
 * @param artistId - The artist ID to retrieve
 * @returns The artist object, or undefined if not found
 */
export const getArtist = (state, artistId) => state.artists.items[artistId]
/**
 * Gets all artists with their IDs resolved to full objects.
 * @param state - The Redux state
 * @returns Artists object with list containing full artist objects instead of IDs
 */
export const getArtists = createSelector(
  (state: State) => state.artists as ArtistsState,
  (artists: ArtistsState) => ({
    ...artists,
    list: artists.list.map((id: ArtistId) => artists.items[id]),
  }),
)
/**
 * Gets the showcase playlists for a specific artist.
 * @param state - The Redux state
 * @param artistId - The artist ID
 * @returns Array of showcase playlist objects, or empty array if none
 */
export const getArtistShowcases = createSelector(
  [getArtist, getPlaylists],
  (
    artist: ReturnType<typeof getArtist>,
    playlists: ReturnType<typeof getPlaylists>,
  ) =>
    artist && artist.showcase_playlist_ids
      ? artist.showcase_playlist_ids
          .map((id: PlaylistId) => playlists[id])
          .filter(Boolean)
      : [],
)
/**
 * Gets the tracks for a specific artist with full track objects.
 * @param state - The Redux state
 * @param artistId - The artist ID
 * @returns Object containing track list with full track objects, or null if no tracks
 */
export const getArtistTracks = createSelector(
  [getArtist, getTracks],
  (
    artist: ReturnType<typeof getArtist>,
    tracks: ReturnType<typeof getTracks>,
  ) =>
    artist?.tracks
      ? {
          ...artist.tracks,
          list: artist.tracks.list
            .map((id: TrackId) => tracks[id])
            .filter(Boolean),
        }
      : null,
)
/**
 * Gets the related artists for a specific artist.
 * @param state - The Redux state
 * @param artistId - The artist ID
 * @returns Array of related artist objects, or empty array if none
 */
export const getRelatedArtists = createSelector(
  [getArtist, getArtists],
  (
    artist: ReturnType<typeof getArtist>,
    artists: ReturnType<typeof getArtists>,
  ) =>
    artist && artist.related_artist_ids
      ? artist.related_artist_ids
          .map((id: ArtistId) => artists.items[id])
          .filter(Boolean)
      : [],
)
/**
 * Gets related artists formatted for dropdown options.
 * @param state - The Redux state
 * @param artistId - The artist ID
 * @returns Array of dropdown option objects with label and value properties
 */
export const getRelatedArtistsDropdownOptions = createSelector(
  [getRelatedArtists],
  (artists: ReturnType<typeof getRelatedArtists>) =>
    artists.map((artist: Artist) => ({
      label: artist ? artist.name : '...',
      value: artist ? artist.id : '',
    })),
)
/**
 * Checks if an artist exists in the state.
 * @param state - The Redux state
 * @param artistId - The artist ID to check
 * @returns True if the artist exists, false otherwise
 */
export const isArtistExists = (state, artistId) => {
  if (!artistId) return false
  const artist = getArtist(state, artistId)
  return Boolean(artistId && !artist?.loading && artist?.id)
}
